import { computed, type MaybeRef, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { AdminPayoutApprovalPreview } from "@shopexperts/legacy-api";
import { extractStatus } from "@/app/composables/claim/claimApiErrors";
import { payoutRequestErrorMessage } from "./payoutRequestError";

interface UseAdminPayoutApprovalPreviewOptions {
  payoutRequestId: MaybeRef<number | null | undefined>;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/** The API answers 409 (not pending) or 422 (balance no longer covers it) when approval would fail. */
export function isApprovalBlockedError(error: unknown): boolean {
  const status = extractStatus(error);
  return status === 409 || status === 422;
}

export function useAdminPayoutApprovalPreview(options: UseAdminPayoutApprovalPreviewOptions) {
  const api = useApi();
  const payoutRequestId = computed(() => unref(options.payoutRequestId));
  const enabled = computed(() => (unref(options.enabled) ?? true) && isPositiveId(payoutRequestId.value));

  const query = useQuery({
    queryKey: computed(() => ["admin", "payout-requests", payoutRequestId.value, "approval-preview"]),
    queryFn: async (): Promise<AdminPayoutApprovalPreview> => {
      if (!isPositiveId(payoutRequestId.value)) {
        throw new Error("payoutRequestId is required to preview a payout approval");
      }

      return await api.admin.wallet.previewPayoutRequestApproval({
        params: { payoutRequest: payoutRequestId.value },
      });
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: (failureCount, error) => !isApprovalBlockedError(error) && failureCount < 1,
  });

  const isBlocked = computed(() => isApprovalBlockedError(query.error.value));
  const blockedMessage = computed(() => (isBlocked.value ? payoutRequestErrorMessage(query.error.value) : null));

  return {
    preview: computed(() => query.data.value ?? null),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isBlocked,
    blockedMessage,
    refetch: query.refetch,
    query,
  };
}
